import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import { WorkflowNotice } from 'components/Workflows/StandardWorkflowLayout';
import React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Database as DatabaseIcon,
  Package,
  Bell,
  AlertTriangle,
  Flag,
  Activity,
  Truck,
  Plus,
  DollarSign,
  Receipt,
  AlertCircle,
  Cable,
  Newspaper,
  FileStack,
  RefreshCw,
  Check,
  Trash2,
  Lock,
} from './icons';
import type { IconProps } from './icons';
import { Button, cx } from './ui';
import { formatDate } from './lib/store';
import { useImportHubReadOnly } from './lib/access';
import { IMPORT_ENTRIES } from './lib/import-data';
import {
  ImportProductsTable,
  type ImportTableServerQuery,
} from './ImportProductsTable';
import { KpiTile } from './primitives';
import { EntriesTable } from './EntriesTable';
import { AddProductsDialog } from './AddProductsDialog';
import { CreateImportDatabaseDialog } from './CreateImportDatabaseDialog';
import { IntegrationsPanel, RegulationsPanel } from './HubEcosystemPanels';
import { Product360Search } from './Product360Search';
import { AceCatalogMatch } from './AceCatalogMatch';
import ImportTools from './tools/ImportTools';
import {
  defaultRenderProductLink,
  defaultRenderDatabaseLink,
  defaultRenderHrefLink,
} from './routing';
import type {
  RenderProductLink,
  RenderDatabaseLink,
  RenderHrefLink,
} from './routing';
import {
  fetchImportDatabasesByGroupId,
  dismissImportDatabaseNotification,
  fetchImportGroupNotifications,
  fetchImportProductsByGroupId,
  fetchImportProductsSummaryByGroupId,
  markImportDatabaseNotificationRead,
  markAllImportGroupNotificationsRead,
  toImportProduct,
} from './lib/import-service';
import type {
  ImportDatabaseNotification,
  ImportServiceDatabase,
  ImportServiceProduct,
  ImportProductListQuery,
  ImportProductListSummary,
} from './lib/import-service';
import { toast } from './lib/toast';
import {
  getImportDatabaseAccent,
  getImportDatabaseIcon,
  resolveImportDatabaseAccent,
} from './databaseAppearance';

type IconComp = ComponentType<IconProps>;

export type ImportHubTab =
  | 'databases'
  | 'products'
  | 'entries'
  | 'notifications'
  | 'documents'
  | 'integrations'
  | 'regulations';

export const IMPORT_HUB_TABS: ImportHubTab[] = [
  'databases',
  'products',
  'entries',
  'notifications',
  'documents',
  'integrations',
  'regulations',
];

export interface ImportHubL1Props {
  /** Initial active tab. */
  initialTab?: ImportHubTab;
  /** Optional: notified when the tab changes so host can sync URL search params. */
  onTabChange?: (tab: ImportHubTab) => void;
  /** How to render a link to a product detail page. Defaults to <a href>. */
  renderProductLink?: RenderProductLink;
  /** How to render a link to a database detail page. Defaults to <a href>. */
  renderDatabaseLink?: RenderDatabaseLink;
  /** How to render arbitrary <a href> links. */
  renderHrefLink?: RenderHrefLink;
  /** Called after the wizard creates a product from the Products Overview tab. */
  onProductCreated?: (id: string) => void;
  /** Team/group id used to scope product-service data (for example cur_user.selected_team.id). */
  groupId?: string;
  /** cur_user.basics.id — required to create a real database from the wizard. */
  createdBy?: string;
}

export const ImportHubL1: React.FC<ImportHubL1Props> = ({
  initialTab = 'databases',
  onTabChange,
  renderProductLink = defaultRenderProductLink,
  renderDatabaseLink = defaultRenderDatabaseLink,
  renderHrefLink = defaultRenderHrefLink,
  onProductCreated,
  groupId,
  createdBy,
}) => {
  const [tab, setActiveTab] = useState<ImportHubTab>(initialTab);
  // Keep the visible tab in sync when the host's route changes while this
  // component stays mounted (browser Back/Forward, or a link to another
  // /import-hub/:tab) — initialTab is only a seed for the first render otherwise.
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [unread, setUnread] = useState(0);
  const [notificationRefreshVersion, setNotificationRefreshVersion] =
    useState(0);

  useEffect(() => {
    if (!groupId) {
      setUnread(0);
      return;
    }
    // The mounted NotificationsList already returns the authoritative unread
    // count with its page request. Avoid a second counts-only poll while that
    // tab is visible.
    if (tab === 'notifications') return;
    let cancelled = false;
    let inFlight = false;
    let activeController: AbortController | null = null;
    const refreshUnread = () => {
      if (inFlight) return;
      inFlight = true;
      activeController = new AbortController();
      fetchImportGroupNotifications(
        groupId,
        'all',
        false,
        1,
        0,
        true,
        activeController.signal,
      )
        .then(({ success, counts }) => {
          if (!cancelled && success) setUnread(counts.unread);
        })
        .finally(() => {
          inFlight = false;
        });
    };
    refreshUnread();
    const interval = window.setInterval(refreshUnread, 15_000);
    return () => {
      cancelled = true;
      activeController?.abort();
      window.clearInterval(interval);
    };
  }, [groupId, notificationRefreshVersion, tab]);

  const setTab = (next: ImportHubTab) => {
    setActiveTab(next);
    onTabChange?.(next);
  };

  return (
    <div className='ih-page ih-hub-shell'>
      <header className='ih-page-header ih-hub-header'>
        <div>
          <h1 className='ih-page-title'>Import Hub</h1>
          <p className='ih-page-subtitle'>
            Monitor PGA flags, Section 301/232 tariff exposure, UFLPA risk, and
            FTA eligibility across your import portfolio.
          </p>
        </div>
        <div className='ih-hub-header-actions'>
          <Product360Search
            hub='import'
            groupId={groupId}
            renderProductLink={renderProductLink}
          />
          <CreditMeterButton packType='database' size='sm' />
        </div>
      </header>

      <nav
        className='ih-tabs ih-hub-tabs'
        role='tablist'
        aria-label='Import Hub sections'
      >
        <TopTab
          active={tab === 'databases'}
          onClick={() => setTab('databases')}
          icon={DatabaseIcon}
          label='Databases'
        />
        <TopTab
          active={tab === 'products'}
          onClick={() => setTab('products')}
          icon={Package}
          label='Products Overview'
        />
        {/* <TopTab active={tab === "entries"} onClick={() => setTab("entries")} icon={Truck} label="Entry Packages" /> */}
        {/* <TopTab
          active={tab === 'notifications'}
          onClick={() => setTab('notifications')}
          icon={Bell}
          label='Notifications'
          badge={unread}
        /> */}
        <TopTab
          active={tab === 'documents'}
          onClick={() => setTab('documents')}
          icon={FileStack}
          label='Doc Upload · Reports · API'
        />
        {/* <TopTab active={tab === "integrations"} onClick={() => setTab("integrations")} icon={Cable} label="Integrations" />
        <TopTab active={tab === "regulations"} onClick={() => setTab("regulations")} icon={Newspaper} label="Regulations" /> */}
      </nav>

      <div role='tabpanel'>
        {tab === 'databases' && (
          <ImportDatabasesGrid
            renderDatabaseLink={renderDatabaseLink}
            groupId={groupId}
            createdBy={createdBy}
          />
        )}
        {tab === 'products' && (
          <ProductsOverviewStub
            renderProductLink={renderProductLink}
            onProductCreated={onProductCreated}
            groupId={groupId}
          />
        )}
        {tab === 'entries' && (
          <EntriesOverview renderProductLink={renderProductLink} />
        )}
        {tab === 'notifications' && (
          <NotificationsList
            groupId={groupId}
            renderHrefLink={renderHrefLink}
            refreshVersion={notificationRefreshVersion}
            onRefresh={() =>
              setNotificationRefreshVersion((current) => current + 1)
            }
            onUnreadChange={setUnread}
          />
        )}
        {tab === 'documents' && <ImportTools />}
        {tab === 'integrations' && (
          <div className='ih-col ih-gap-4'>
            <AceCatalogMatch />
            <IntegrationsPanel hub='import' />
          </div>
        )}
        {tab === 'regulations' && <RegulationsPanel hub='import' />}
      </div>
    </div>
  );
};

const TopTab: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: IconComp;
  label: string;
  badge?: number;
}> = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    type='button'
    role='tab'
    aria-selected={active}
    data-active={active}
    onClick={onClick}
    className='ih-tab'
  >
    <Icon className='ih-hub-tab-icon' size={16} /> {label}
    {!!badge && badge > 0 && (
      <span className='ih-badge ih-badge-danger'>{badge}</span>
    )}
  </button>
);

/* ============================== Databases grid ============================== */
const ImportDatabasesGrid: React.FC<{
  renderDatabaseLink: RenderDatabaseLink;
  groupId?: string;
  createdBy?: string;
}> = ({ renderDatabaseLink, groupId, createdBy }) => {
  const readOnly = useImportHubReadOnly();
  const history = useHistory();
  const [createOpen, setCreateOpen] = useState(false);
  // Bumped after a real database is created (CreateImportDatabaseDialog's
  // onCreated below) to force the real-database list to refetch and pick it up.
  const [bump, setBump] = useState(0);

  // Real databases fetched from the product service
  // (GET /database/group-id/:groupId).
  const [serviceDatabases, setServiceDatabases] = useState<
    ImportServiceDatabase[]
  >([]);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [databasePage, setDatabasePage] = useState(1);
  const [databaseTotal, setDatabaseTotal] = useState(0);
  const databasePageSize = 12;
  const databaseGroupRef = useRef<string | undefined>(groupId);

  useEffect(() => {
    if (!groupId) {
      databaseGroupRef.current = undefined;
      setServiceDatabases([]);
      setDatabaseTotal(0);
      setDatabasePage(1);
      setServiceLoading(false);
      setServiceError(null);
      return;
    }

    const groupChanged = databaseGroupRef.current !== groupId;
    if (groupChanged) {
      databaseGroupRef.current = groupId;
      setServiceDatabases([]);
      setDatabaseTotal(0);
      setServiceError(null);
      if (databasePage !== 1) {
        setServiceLoading(true);
        setDatabasePage(1);
        return;
      }
    }

    let cancelled = false;
    const controller = new AbortController();
    setServiceLoading(true);
    setServiceError(null);
    fetchImportDatabasesByGroupId(
      groupId,
      databasePage,
      databasePageSize,
      controller.signal,
    ).then(({ success, databases, total, error }) => {
      if (cancelled) return;
      if (success) {
        setDatabaseTotal(total);
        const lastPage = Math.max(1, Math.ceil(total / databasePageSize));
        if (databasePage > lastPage) {
          setDatabasePage(lastPage);
          return;
        }
        setServiceLoading(false);
        setServiceDatabases(databases);
      } else {
        setServiceLoading(false);
        const message = error ?? 'Failed to load databases';
        setServiceError(message);
        toast.error(message);
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [groupId, bump, databasePage]);

  const databasePageCount = Math.max(
    1,
    Math.ceil(databaseTotal / databasePageSize),
  );

  return (
    <section className='ih-hub-databases-section'>
      <div className='ih-hub-section-header'>
        <div>
          <h2 className='ih-hub-section-title'>Databases</h2>
          <p className='ih-hub-section-subtitle'>
            Scoped workspaces for import products, shipments, and tariff
            exposure. Open one to drill in.
          </p>
        </div>
        <div className='ih-hub-section-actions'>
          <Button
            variant='primary'
            className='ih-hub-primary-action'
            locked={readOnly}
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={18} /> Create Database
          </Button>
        </div>
      </div>

      {serviceLoading && serviceDatabases.length === 0 ? (
        <div className='ih-db-grid' aria-label='Loading databases'>
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              className='ih-db-card ih-db-card-skeleton'
              key={`database-skeleton-${index}`}
            >
              <div className='ih-db-skeleton ih-db-skeleton-icon' />
              <div className='ih-db-skeleton ih-db-skeleton-line is-short' />
              <div className='ih-db-skeleton ih-db-skeleton-line' />
              <div className='ih-db-skeleton ih-db-skeleton-stats' />
            </div>
          ))}
        </div>
      ) : serviceError && serviceDatabases.length === 0 ? (
        <div className='ih-card ih-notification-state'>
          <AlertCircle size={18} />
          <strong>Databases could not be loaded</strong>
          <span>{serviceError}</span>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setBump((current) => current + 1)}
          >
            Try Again
          </Button>
        </div>
      ) : serviceDatabases.length === 0 ? (
        <div className='ih-card ih-db-empty'>
          <h3 className='ih-db-empty-title'>No databases yet</h3>
          <p className='ih-db-empty-sub'>
            Create your first database to start organizing import products,
            tariff exposure, and PGA compliance.
          </p>
          <Button
            variant='primary'
            className='ih-hub-primary-action'
            locked={readOnly}
            onClick={() => setCreateOpen(true)}
            style={{ marginTop: 16 }}
          >
            <Plus size={18} /> Create Database
          </Button>
        </div>
      ) : (
        <>
          {serviceLoading && (
            <div className='ih-text-xs ih-text-muted' role='status'>
              Refreshing databases…
            </div>
          )}
          {serviceError && (
            <div className='ih-edit-error' role='alert'>
              {serviceError}{' '}
              <button
                type='button'
                onClick={() => setBump((current) => current + 1)}
              >
                Try again
              </button>
            </div>
          )}
          <div className='ih-db-grid'>
            {serviceDatabases.map((db) => {
              const CardIcon = getImportDatabaseIcon(db.icon);
              const storedAccent = getImportDatabaseAccent(db.accent_color);
              const accent = resolveImportDatabaseAccent(storedAccent);
              return (
                <React.Fragment key={db.id}>
                  {renderDatabaseLink({
                    dbId: db.id,
                    className: 'ih-db-card',
                    children: (
                      <>
                        <div className='ih-db-card-head'>
                          <div
                            className='ih-db-card-icon'
                            style={{
                              background: `color-mix(in oklab, ${accent} 18%, transparent)`,
                              color: accent,
                            }}
                          >
                            <CardIcon size={22} />
                          </div>
                        </div>
                        <div>
                          <h3 className='ih-db-card-title'>{db.name}</h3>
                          <p className='ih-db-card-desc'>
                            {db.description || '—'}
                          </p>
                        </div>
                        <div className='ih-db-card-stats'>
                          <MiniStat
                            icon={Package}
                            label='Products'
                            value={db.nr_products ?? 0}
                          />
                          <MiniStat
                            icon={Flag}
                            label='Issues'
                            value={db.total_issues_products ?? 0}
                          />
                          <MiniStat
                            icon={AlertTriangle}
                            label='High risk'
                            value={db.nr_high_risk_products ?? 0}
                          />
                        </div>
                        <div className='ih-db-card-foot'>
                          <span className='ih-num'>
                            Created{' '}
                            {db.created_at ? formatDate(db.created_at) : '—'}
                          </span>
                        </div>
                      </>
                    ),
                  })}
                </React.Fragment>
              );
            })}
            <button
              className='ih-db-create-tile'
              type='button'
              disabled={readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
              onClick={() => setCreateOpen(true)}
            >
              {readOnly ? <Lock size={24} /> : <Plus size={24} />}
              <span>Create Database</span>
            </button>
          </div>
        </>
      )}
      {serviceDatabases.length > 0 && databaseTotal > databasePageSize && (
        <div className='ih-priority-pagination' aria-label='Database pages'>
          <Button
            variant='outline'
            size='sm'
            disabled={serviceLoading || databasePage <= 1}
            onClick={() => setDatabasePage((page) => Math.max(1, page - 1))}
          >
            Previous
          </Button>
          <span className='ih-text-xs ih-text-muted ih-num'>
            Page {databasePage} of {databasePageCount} · {databaseTotal}{' '}
            databases
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={serviceLoading || databasePage >= databasePageCount}
            onClick={() =>
              setDatabasePage((page) => Math.min(databasePageCount, page + 1))
            }
          >
            Next
          </Button>
        </div>
      )}
      {/* Legal disclaimer -- the same WorkflowNotice the Report Builder uses. */}
      <div className='ih-hub-disclaimer'>
        <WorkflowNotice>
          <p>
            BITE provides automated trade compliance analysis based on the
            product information supplied, applicable trade regulations, and
            available data. Results are advisory and should be reviewed before
            use in customs declarations or filings.
          </p>
          <p>
            By using this module, you confirm that you are the importer of
            record, an authorized customs broker, or otherwise authorized to
            evaluate the products on behalf of the responsible party. The
            importer of record remains responsible for the accuracy of
            classifications, declared values, country of origin, applicable
            duties, and regulatory requirements.
          </p>
        </WorkflowNotice>
      </div>
      <CreateImportDatabaseDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setDatabasePage(1);
          setBump((n) => n + 1);
        }}
        onCreatedForDocumentUpload={(db, file) => {
          history.push('/customs-entry-analyzer', {
            presetDatabaseId: db.id,
            presetDatabaseName: db.name,
            presetFile: file,
            returnTo: { label: db.name, path: `/import-hub/db/${db.id}` },
          });
        }}
        groupId={groupId}
        createdBy={createdBy}
      />
    </section>
  );
};

const MiniStat: React.FC<{
  icon: IconComp;
  label: string;
  value: number;
}> = ({ icon: Icon, label, value }) => (
  <div className='ih-mini-stat'>
    <div className='ih-mini-stat-value ih-num'>{value}</div>
    <div className='ih-mini-stat-label'>
      <Icon className='ih-mini-stat-icon' size={12} />
      <span>{label}</span>
    </div>
  </div>
);

/* ============================== Products Overview (KPIs only) ============================== */
const ProductsOverviewStub: React.FC<{
  renderProductLink: RenderProductLink;
  onProductCreated?: (id: string) => void;
  groupId?: string;
}> = ({ renderProductLink, onProductCreated, groupId }) => {
  const readOnly = useImportHubReadOnly();
  const [addOpen, setAddOpen] = useState(false);

  // Real cross-database products, fetched via GET /product/group-id/:groupId.
  // `null` means the request has not returned; an empty array is a definitive
  // empty state and never falls through to demo records.
  const [realProducts, setRealProducts] = useState<
    ImportServiceProduct[] | null
  >(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productFacets, setProductFacets] = useState<{
    origins: string[];
    databases: Array<{ id: string; name: string }>;
  }>({ origins: [], databases: [] });
  const productFacetsGroupRef = useRef<string | null>(null);
  const [productQuery, setProductQuery] = useState<ImportProductListQuery>({
    sortBy: 'compliance_score',
    sortOrder: 'desc',
  });
  const [summary, setSummary] = useState<ImportProductListSummary | null>(null);
  const [summaryGroupId, setSummaryGroupId] = useState<string | null>(null);
  const productPageSize = 25;

  useEffect(() => {
    setRealProducts(null);
    setProductTotal(0);
    setProductFacets({ origins: [], databases: [] });
    productFacetsGroupRef.current = null;
    setProductsError(null);
    setProductPage(1);
    setSummary(null);
    setSummaryGroupId(null);
  }, [groupId]);

  useEffect(() => {
    if (!groupId) {
      setRealProducts([]);
      setProductTotal(0);
      setProductsError(null);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setProductsLoading(true);
    setProductsError(null);
    fetchImportProductsByGroupId(
      groupId,
      {
        ...productQuery,
        limit: productPageSize,
        offset: (productPage - 1) * productPageSize,
        includeFacets: productFacetsGroupRef.current !== groupId,
      },
      controller.signal,
    ).then(
      ({
        success,
        products: fetched,
        total,
        facets,
        facetsIncluded,
        error,
      }) => {
        if (cancelled) return;
        setProductsLoading(false);
        if (success) {
          const lastPage = Math.max(1, Math.ceil(total / productPageSize));
          if (productPage > lastPage) {
            setProductPage(lastPage);
            return;
          }
          setRealProducts(fetched);
          setProductTotal(total);
          if (facetsIncluded) {
            setProductFacets(facets);
            productFacetsGroupRef.current = groupId;
          }
        } else {
          const message = error ?? 'Failed to load products';
          setProductsError(message);
          toast.error(message);
        }
      },
    );
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [groupId, productPage, productQuery]);

  useEffect(() => {
    setSummary(null);
    if (!groupId) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    fetchImportProductsSummaryByGroupId(groupId, controller.signal).then(
      ({ success, summary: result }) => {
        if (!cancelled && success) {
          setSummary(result);
          setSummaryGroupId(groupId);
        }
      },
    );
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [groupId]);

  const handleServerQuery = useCallback((query: ImportTableServerQuery) => {
    const sortByMap: Record<ImportTableServerQuery['sortKey'], string> = {
      name: 'name',
      hts: 'hts_code',
      origin: 'country_of_origin',
      value: 'total_cost',
      baseDuty: 'base_duty_rate',
      addDuty: 'additional_duty_rate',
      flags: 'program_flags',
      score: 'compliance_score',
    };
    const next: ImportProductListQuery = {
      search: query.filters.q,
      risk: query.filters.risk,
      pga: query.filters.pga,
      tariff: query.filters.tariff,
      valueBuckets: query.filters.value,
      fta: query.filters.fta,
      origins: query.filters.origin,
      databaseIds: query.filters.database,
      additionalDutyBuckets: query.filters.addDuty,
      scoreBuckets: query.filters.score,
      classification: query.filters.classification,
      issueTypes: query.filters.issues,
      sortBy: sortByMap[query.sortKey],
      sortOrder: query.sortDir,
    };
    setProductQuery((current) => {
      if (JSON.stringify(current) === JSON.stringify(next)) return current;
      setProductPage(1);
      return next;
    });
  }, []);

  const products = (realProducts ?? []).map((row) =>
    toImportProduct(row, row.database_id ?? ''),
  );

  const scopedSummary = summaryGroupId === groupId ? summary : null;
  const total = scopedSummary?.total ?? productTotal;
  const high = scopedSummary?.highRisk ?? 0;
  const assessedProducts = products.filter(
    (product) => product.complianceAssessed !== false,
  );
  const avgScore = scopedSummary
    ? scopedSummary.averageComplianceScore === null
      ? '—'
      : String(scopedSummary.averageComplianceScore)
    : assessedProducts.length > 0
    ? String(
        Math.round(
          assessedProducts.reduce(
            (sum, product) => sum + product.complianceScore,
            0,
          ) / assessedProducts.length,
        ),
      )
    : '—';
  const flagged = scopedSummary?.flagged ?? 0;
  const issues = scopedSummary?.totalIssues ?? 0;
  const totalValue = scopedSummary?.totalValue ?? 0;
  const totalDuty = scopedSummary?.totalDuty ?? 0;
  const hasActiveProductFilters = Object.entries(productQuery).some(
    ([key, value]) =>
      key !== 'sortBy' &&
      key !== 'sortOrder' &&
      (Array.isArray(value) ? value.length > 0 : Boolean(value)),
  );
  const fmt = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  return (
    <div className='ih-col ih-gap-4'>
      <div className='ih-row-wrap ih-between ih-gap-3'>
        <div>
          <h2 className='ih-section-title' style={{ fontSize: 16 }}>
            Products Overview
          </h2>
          <p className='ih-text-xs ih-text-muted ih-mt-1'>
            All products across databases.
          </p>
        </div>
        <div className='ih-row ih-gap-3' style={{ alignItems: 'center' }}>
          {productsLoading && (
            <span
              className='ih-row ih-gap-1 ih-text-xs ih-text-muted'
              style={{ alignItems: 'center' }}
            >
              <span
                className='ih-spinner'
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
              Loading live products…
            </span>
          )}
          <Button
            variant='primary'
            size='sm'
            locked={readOnly}
            onClick={() => setAddOpen(true)}
          >
            <Plus size={14} /> Add Products
          </Button>
        </div>
      </div>
      <div className='ih-kpi-cluster'>
        <KpiTile icon={Package} label='Total Products' value={String(total)} />
        <KpiTile icon={Activity} label='Avg Score' value={avgScore} />
        <KpiTile
          icon={AlertTriangle}
          label='High Risk'
          value={String(high)}
          tone='danger'
        />
        <KpiTile
          icon={Flag}
          label='Total Flagged'
          value={String(flagged)}
          tone='warning'
        />
        <KpiTile
          icon={AlertCircle}
          label='Total Issues'
          value={String(issues)}
          tone={issues > 0 ? 'danger' : undefined}
        />
        <KpiTile
          icon={DollarSign}
          label='Total Value'
          value={fmt(totalValue)}
        />
        <KpiTile icon={Receipt} label='Total Duty' value={fmt(totalDuty)} />
      </div>
      {productsLoading && realProducts === null ? (
        <div className='ih-card ih-notification-state'>
          <span className='ih-spinner ih-notification-spinner' />
          Loading products…
        </div>
      ) : productsError && realProducts === null ? (
        <div className='ih-card ih-notification-state'>
          <AlertCircle size={18} />
          <strong>Products could not be loaded</strong>
          <span>{productsError}</span>
        </div>
      ) : products.length === 0 && !hasActiveProductFilters ? (
        <div className='ih-card ih-notification-state'>
          <Package size={18} />
          <strong>No products yet</strong>
          <span>Add a product or upload a database to populate this view.</span>
        </div>
      ) : (
        <ImportProductsTable
          products={products}
          serverSide
          totalProducts={productTotal}
          page={productPage}
          pageSize={productPageSize}
          loading={productsLoading}
          facets={productFacets}
          onPageChange={setProductPage}
          onServerQueryChange={handleServerQuery}
          showDatabaseColumn
          showDatabaseFilter
          renderProductLink={renderProductLink}
        />
      )}
      <AddProductsDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        groupId={groupId}
        onProductCreated={onProductCreated}
      />
    </div>
  );
};

const EntriesOverview: React.FC<{ renderProductLink: RenderProductLink }> = ({
  renderProductLink,
}) => (
  <div className='ih-col ih-gap-3'>
    <div>
      <h2 className='ih-section-title' style={{ fontSize: 16 }}>
        Entry Packages
      </h2>
      <p className='ih-text-xs ih-text-muted ih-mt-1'>
        Import events with entry metadata, products, and their supporting
        documents over time.
      </p>
    </div>
    <EntriesTable
      entries={IMPORT_ENTRIES}
      showDatabaseColumn
      renderProductLink={renderProductLink}
    />
  </div>
);

const NotificationsList: React.FC<{
  groupId?: string;
  renderHrefLink: RenderHrefLink;
  refreshVersion: number;
  onRefresh: () => void;
  onUnreadChange: (count: number) => void;
}> = ({
  groupId,
  renderHrefLink,
  refreshVersion,
  onRefresh,
  onUnreadChange,
}) => {
  const readOnly = useImportHubReadOnly();
  const [notifications, setNotifications] = useState<
    ImportDatabaseNotification[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const unreadCountRef = useRef(0);
  const quietRefreshRef = useRef(false);
  const pageSize = 25;

  useEffect(() => setPage(1), [groupId]);

  useEffect(() => {
    if (!groupId) {
      setNotifications([]);
      setError(null);
      unreadCountRef.current = 0;
      onUnreadChange(0);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const quietRefresh = quietRefreshRef.current;
    quietRefreshRef.current = false;
    if (!quietRefresh) setLoading(true);
    setError(null);
    fetchImportGroupNotifications(
      groupId,
      'all',
      false,
      pageSize,
      (page - 1) * pageSize,
      false,
      controller.signal,
    ).then(
      ({
        success,
        notifications: result,
        counts,
        total: nextTotal,
        error: fetchError,
      }) => {
        if (cancelled) return;
        setLoading(false);
        if (!success) {
          if (!quietRefresh) {
            setNotifications([]);
            setError(fetchError ?? 'Failed to load team notifications');
          }
          return;
        }
        if (result.length === 0 && nextTotal > 0 && page > 1) {
          setPage((current) => Math.max(1, current - 1));
          return;
        }
        setNotifications(result);
        setTotal(nextTotal);
        unreadCountRef.current = counts.unread;
        onUnreadChange(counts.unread);
      },
    );
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [groupId, page, refreshVersion, onUnreadChange]);

  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!groupId) return;
    const interval = window.setInterval(() => {
      quietRefreshRef.current = true;
      onRefreshRef.current();
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [groupId]);

  const markRead = async (notification: ImportDatabaseNotification) => {
    if (notification.read || busyId || !notification.databaseId) return;
    const previousNotifications = notifications;
    const previousUnread = unreadCountRef.current;
    const nextUnread = Math.max(0, previousUnread - 1);

    setNotifications((current) =>
      current.map((item) =>
        item.id === notification.id ? { ...item, read: true } : item,
      ),
    );
    unreadCountRef.current = nextUnread;
    onUnreadChange(nextUnread);
    setBusyId(notification.id);
    try {
      const result = await markImportDatabaseNotificationRead(
        notification.databaseId,
        notification.id,
      );
      if (!result.success) {
        setNotifications(previousNotifications);
        unreadCountRef.current = previousUnread;
        onUnreadChange(previousUnread);
        toast.error(result.error ?? 'Failed to mark notification as read');
        return;
      }
      quietRefreshRef.current = true;
      onRefresh();
    } catch (err) {
      setNotifications(previousNotifications);
      unreadCountRef.current = previousUnread;
      onUnreadChange(previousUnread);
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to mark notification as read',
      );
    } finally {
      setBusyId(null);
    }
  };

  const markAllRead = async () => {
    if (!groupId || markingAll || unreadCountRef.current === 0) return;
    const previousNotifications = notifications;
    const previousUnread = unreadCountRef.current;

    setNotifications((current) =>
      current.map((item) => ({ ...item, read: true })),
    );
    unreadCountRef.current = 0;
    onUnreadChange(0);
    setMarkingAll(true);
    try {
      const result = await markAllImportGroupNotificationsRead(groupId);
      if (!result.success) {
        setNotifications(previousNotifications);
        unreadCountRef.current = previousUnread;
        onUnreadChange(previousUnread);
        toast.error(result.error ?? 'Failed to mark all notifications as read');
        return;
      }
      quietRefreshRef.current = true;
      onRefresh();
    } catch (err) {
      setNotifications(previousNotifications);
      unreadCountRef.current = previousUnread;
      onUnreadChange(previousUnread);
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to mark all notifications as read',
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const dismiss = async (
    event: React.MouseEvent,
    notification: ImportDatabaseNotification,
  ) => {
    event.preventDefault();
    if (busyId || !notification.databaseId) return;
    const previousNotifications = notifications;
    const previousTotal = total;
    const previousUnread = unreadCountRef.current;
    const nextUnread = Math.max(
      0,
      previousUnread - (notification.read ? 0 : 1),
    );

    setNotifications((current) =>
      current.filter((item) => item.id !== notification.id),
    );
    setTotal((current) => Math.max(0, current - 1));
    unreadCountRef.current = nextUnread;
    onUnreadChange(nextUnread);
    setBusyId(notification.id);
    try {
      const result = await dismissImportDatabaseNotification(
        notification.databaseId,
        notification.id,
      );
      if (!result.success) {
        setNotifications(previousNotifications);
        setTotal(previousTotal);
        unreadCountRef.current = previousUnread;
        onUnreadChange(previousUnread);
        toast.error(result.error ?? 'Failed to dismiss notification');
        return;
      }
      quietRefreshRef.current = true;
      onRefresh();
    } catch (err) {
      setNotifications(previousNotifications);
      setTotal(previousTotal);
      unreadCountRef.current = previousUnread;
      onUnreadChange(previousUnread);
      toast.error(
        err instanceof Error ? err.message : 'Failed to dismiss notification',
      );
    } finally {
      setBusyId(null);
    }
  };

  const TYPE_META: Record<string, { label: string; cls: string }> = {
    screening: { label: 'Screening', cls: 'ih-badge-info' },
    'pga-hold': { label: 'PGA hold', cls: 'ih-badge-warning' },
    'tariff-change': { label: 'Tariff change', cls: 'ih-badge-info' },
    'uflpa-flag': { label: 'UFLPA flag', cls: 'ih-badge-danger' },
    'fta-expiry': { label: 'FTA expiry', cls: 'ih-badge-warning' },
    detention: { label: 'Detention', cls: 'ih-badge-danger' },
  };
  return (
    <div className='ih-col ih-gap-2'>
      <div className='ih-row ih-between ih-gap-2'>
        <div>
          <h2 className='ih-section-title' style={{ fontSize: 16 }}>
            Notifications
          </h2>
          <p className='ih-text-xs ih-text-muted ih-mt-1'>
            Actionable updates across your team&apos;s product databases.
          </p>
        </div>
        <div className='ih-row ih-gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={markAllRead}
            disabled={loading || markingAll || unreadCountRef.current === 0}
            locked={readOnly}
          >
            <Check size={13} /> Mark All as Read
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw size={13} /> Refresh
          </Button>
        </div>
      </div>
      {loading ? (
        <div className='ih-card ih-notification-state'>
          <span className='ih-spinner ih-notification-spinner' />
          Loading notifications…
        </div>
      ) : error ? (
        <div className='ih-card ih-notification-state'>
          <AlertCircle size={18} />
          <strong>Notifications could not be loaded</strong>
          <span>{error}</span>
          <Button variant='outline' size='sm' onClick={onRefresh}>
            Try Again
          </Button>
        </div>
      ) : notifications.length === 0 ? (
        <div className='ih-card ih-notification-state'>
          <Bell size={18} />
          <strong>No notifications yet</strong>
          <span>
            Screening and material compliance updates will appear here.
          </span>
        </div>
      ) : (
        <div className='ih-notification-list'>
          {notifications.map((notification) => {
            const meta = TYPE_META[notification.type] ?? {
              label: notification.system ? 'System' : 'User',
              cls: notification.system ? 'ih-badge-info' : 'ih-badge-muted',
            };
            return (
              <div
                key={notification.id}
                className={cx(
                  'ih-notif-item',
                  !notification.read && 'is-unread',
                )}
              >
                <span
                  className={cx(
                    'ih-notif-dot',
                    !notification.read && 'is-unread',
                  )}
                />
                <div className='ih-grow' style={{ minWidth: 0 }}>
                  <div className='ih-row-wrap ih-gap-2'>
                    <span className={cx('ih-badge', meta.cls)}>
                      {meta.label}
                    </span>
                    {notification.link ? (
                      renderHrefLink({
                        href: notification.link,
                        className: 'ih-notification-link',
                        children: notification.heading,
                      })
                    ) : (
                      <div className='ih-text-sm ih-font-medium'>
                        {notification.heading}
                      </div>
                    )}
                  </div>
                  <p className='ih-text-xs ih-text-muted ih-mt-1'>
                    {notification.message}
                  </p>
                  <div className='ih-notification-meta'>
                    <span>{notification.sender || 'System'}</span>
                    <span>·</span>
                    <span>
                      {notification.createdAt
                        ? formatDate(notification.createdAt)
                        : 'Date unavailable'}
                    </span>
                  </div>
                </div>
                <div className='ih-notification-actions'>
                  {!notification.read && (
                    <Button
                      variant='ghost'
                      size='sm'
                      locked={readOnly}
                      onClick={() => void markRead(notification)}
                      disabled={busyId === notification.id}
                    >
                      <Check size={13} /> Read
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    size='icon'
                    title='Dismiss notification'
                    locked={readOnly}
                    onClick={(event) => void dismiss(event, notification)}
                    disabled={busyId === notification.id}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && !error && total > pageSize && (
        <div className='ih-priority-pagination' aria-label='Notification pages'>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <span className='ih-text-xs ih-text-muted ih-num'>
            Page {page} of {Math.ceil(total / pageSize)} · {total} notifications
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= Math.ceil(total / pageSize)}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
